<template>
    <div class="ai-image-website-content max-z-index" v-show="isShowHandleAi">
		<!-- Show loader while the iframe is loading -->
		<div class="iframe-loading" v-loading="isLoading" v-if="isLoading">
        <!-- <img src="/static/img/ajax-loader.gif" alt="Loading..." /> -->
	</div>
	<!-- Only render iframe when URL is available -->
	<iframe
        v-show="!!handleEnhanceWithAIUrl"
        width="100%"
        @load="onIframeLoad"
        @error="onIframeError"
        :src="handleEnhanceWithAIUrl"
        frameborder="0"
        ref="aiImageIframe"
        id="aiImageIframe"
        class="ai-image-website-iframe"
	></iframe>
    </div>
</template>
<script>
import { WEBAPP_LAYOUT } from "../../../common/constants";
import { storeModules } from "../../../store";
import { AI_URI } from "../../../common/uri/property-uri.constants";
import { AI_IMAGE_EVENTS } from "../../../common/ai-image.constant";
import { postMessageForWebview } from "../../../utils/web-view";
import {
    createAIImageFlowLogger,
    AIImageFlowStep
} from "../../../utils/graylog-helper";

export default {
    name: 'AIImageWebview',
    data() {
        return {
            aiImageIframe: null,
            handleEnhanceWithAIUrl: '',
            isShowHandleAi: false,
            isLoading: true,
            flowLogger: null, // AI Image Flow Logger instance
            authState: null,
            language: null,
            imageUrl: null
        }
    },
    computed: {
        webappLayoutMode() {
            return this.$store.state[storeModules.Common].webappLayoutMode;
        },
        isWebview() {
            return this.webappLayoutMode === WEBAPP_LAYOUT.WEBVIEW;
        },
    },
    methods: {
        onIframeLoad() {
            this.isLoading = false;
            if (this.flowLogger) {
                this.flowLogger.log(AIImageFlowStep.IFRAME_LOADED, {
                    iframeUrl: this.handleEnhanceWithAIUrl ? '[LOADED]' : '[EMPTY]'
                });
            }
        },

        onIframeError(error) {
            this.isLoading = false;
            if (this.flowLogger) {
                this.flowLogger.error(AIImageFlowStep.IFRAME_ERROR, error, {
                    iframeUrl: this.handleEnhanceWithAIUrl
                });
            }
        },

        async authenticateAndLoadAIImageApp() {
            // Log auth start
            if (this.flowLogger) {
                this.flowLogger.log(AIImageFlowStep.AUTH_START, {
                    hasAccessToken: !!this.authState?.oauth2?.accessToken,
                    imageUrl: this.imageUrl,
                    language: this.language
                });
            }

            try {
				if (!this.authState?.oauth2?.accessToken) {
					console.error("Authentication failed: Missing access token.");
					this.$message.error('Authentication failed: Missing access token.');
                    if (this.flowLogger) {
                        this.flowLogger.error(AIImageFlowStep.AUTH_ERROR, 'Missing access token');
                    }
					this.isLoading = false;
					return;
				}

                // Log auth success
                if (this.flowLogger) {
                    this.flowLogger.log(AIImageFlowStep.AUTH_SUCCESS);
                }

				const encodedAuthState = encodeURIComponent(JSON.stringify(this.authState));
				const encodedImageUrl = this.imageUrl ? encodeURIComponent(this.imageUrl) : '';
				const sessionId = this.flowLogger?.sessionId || '';

				this.handleEnhanceWithAIUrl = `${AI_URI.IMAGE_ENHANCEMENT}?authState=${encodedAuthState}&imageUrl=${encodedImageUrl}&language=${this.language}&sessionId=${sessionId}`;

                // Log iframe loading with exact input image URL for debugging
                if (this.flowLogger) {
                    this.flowLogger.log(AIImageFlowStep.IFRAME_LOADING, {
                        targetUrl: AI_URI.IMAGE_ENHANCEMENT,
                        inputImageUrl: this.imageUrl
                    });
                }
			} catch (error) {
				console.error("Error authenticating and loading image enhancement app:", error);
				this.$message.error('Error authenticating and loading image enhancement app.');
                if (this.flowLogger) {
                    this.flowLogger.error(AIImageFlowStep.AUTH_ERROR, error);
                }
				this.isLoading = false;
			}
		},

		async handleMessage(event) {
			console.log("HandleMessage", event);
			const { data } = event;
			const TYPE = data?.TYPE;
			const { type } = data;
			const MessageType = TYPE || type;

            // Log message received from iframe (metadata only, exact image logged at SAVE/REPLACE step)
            if (MessageType && this.flowLogger) {
                this.flowLogger.log(AIImageFlowStep.IFRAME_MESSAGE_RECEIVED, {
                    messageType: MessageType
                });
            }

			switch (MessageType) {
				case AI_IMAGE_EVENTS.CANCEL: {
                    if (this.flowLogger) {
                        this.flowLogger.log(AIImageFlowStep.WINAPP_CANCEL);
                    }
					postMessageForWebview("CANCEL", null, 'AIImageWebview');

                    // Log flow completed
                    if (this.flowLogger) {
                        this.flowLogger.log(AIImageFlowStep.FLOW_COMPLETED, {
                            result: 'CANCELLED'
                        });
                    }
					break;
				}
				case AI_IMAGE_EVENTS.SAVE: {
					const { name, image } = data;
                    if (this.flowLogger) {
                        this.flowLogger.log(AIImageFlowStep.WINAPP_SAVE, {
                            imageName: name,
                            hasImage: !!image
                        });
                    }
					postMessageForWebview("SAVE", { name, image }, 'AIImageWebview');

                    // Log flow completed
                    if (this.flowLogger) {
                        this.flowLogger.log(AIImageFlowStep.FLOW_COMPLETED, {
                            result: 'SAVED',
                            imageName: name
                        });
                    }
					break;
				}
				case AI_IMAGE_EVENTS.REPLACE: {
					const { image } = data;
                    if (this.flowLogger) {
                        this.flowLogger.log(AIImageFlowStep.WINAPP_REPLACE, {
                            hasImage: !!image
                        });
                    }
					postMessageForWebview("REPLACE", { image }, 'AIImageWebview');

                    // Log flow completed
                    if (this.flowLogger) {
                        this.flowLogger.log(AIImageFlowStep.FLOW_COMPLETED, {
                            result: 'REPLACED'
                        });
                    }
					break;
				}
				default: {
					break;
				}
			}
		},

		async initData() {
			if (this.isWebview) {
				const vm = window;
				console.log("Webview config", vm);
				if (vm.chrome && vm.chrome.webview) {
					vm.chrome.webview.addEventListener("message", async (event) => {
						console.log("Listening aiImageInfo");

                        if (event.data.message === "aiImageInfo") {
                            // Create new flow logger for this session
                            this.flowLogger = createAIImageFlowLogger();
                            console.log(`[AIImage] Flow Session ID: ${this.flowLogger.sessionId}`);

                            // Log WINAPP message received
                            this.flowLogger.log(AIImageFlowStep.WINAPP_MESSAGE_RECEIVED, {
                                message: event.data.message,
                                hasAuthState: !!event.data.data?.authState,
                                imageUrl: event.data.data?.imageUrl,
                                language: event.data.data?.language
                            });

                            const { authState, language, imageUrl } = event.data.data;
                            console.log("Receive data from message", event);
                            this.authState = authState;
                            this.language = language;
                            this.imageUrl = imageUrl;

                            await this.authenticateAndLoadAIImageApp();
                            this.handleMessage = this.handleMessage.bind(this);
                            window.addEventListener("message", this.handleMessage);
                        }
                    });
                }

                console.log('Send message to webview');
                postMessageForWebview("COMPLETE_LOADING", null, 'AIImageWebview');

                // Note: COMPLETE_LOADING is sent before we have a flowLogger
                // This is expected - it's the initial handshake
			}
		}
    },
	async created() {
		this.isShowHandleAi = true;
		this.initData();
	},
	beforeDestroy() {
		window.removeEventListener("message", this.handleMessage);

        // Log if flow was interrupted
        if (this.flowLogger) {
            this.flowLogger.log(AIImageFlowStep.FLOW_COMPLETED, {
                result: 'COMPONENT_DESTROYED'
            });
        }
	}
}
</script>
<style scoped>
.ai-image-website-iframe {
    position: fixed;
    top: 0;
    height: 100%;
    width: 100%;
    left: 0;
    z-index: 9999;
}</style>
